import type { ReactElement } from 'react';
import type { TranslationFunction } from '@/app/components/translations/Translation.tsx';
import type { Filter, FiltersGroups, FiltersGroupId } from '@/app/common/filters/core/filtersTypes.ts';
import { CUSTOM_GROUP_NAME, FILTER_NAME } from '@/app/common/filters/core/filterNames.ts';
import type { CustomPillElement, FilterPill } from '@/app/common/filters/surfaces/bar/pills/types.ts';
import { AllFiltersPill } from '@/app/common/filters/surfaces/bar/resultsPills/AllFiltersPill.tsx';
import { SortPill } from '@/app/common/filters/surfaces/bar/resultsPills/SortPill.tsx';
import type { SearchInfoData } from '@/app/search/types.ts';
import type { ProductsData } from '@/app/api/search/products/createProductsData.ts';
import { getPillIcon } from '@/app/common/filters/surfaces/bar/resultsPills/pillIcons.tsx';
import {
  ACCOMMODATION_FACILITY_FILTER_NAMES,
  FACILITY_GROUP_FILTER_NAMES,
} from '@/app/common/filters/groups/facilityGroups.ts';

export type BarPillContext = {
  filtersGroups: FiltersGroups;
  searchInfoData: SearchInfoData;
  productsData: ProductsData;
  t: TranslationFunction;
};

export type BarLayout = 'desktop' | 'mobile';

type BarPillPlacement = {
  layouts?: BarLayout[];
  pinned?: true;
  leadsBar?: true;
};

export type GroupBarPill = BarPillPlacement & {
  id: FiltersGroupId;
  filterNames: FILTER_NAME[];
  columns?: number;
};

export type CustomBarPill = BarPillPlacement & {
  id: FiltersGroupId;
  CustomPill: CustomPillElement;
  withIcon?: boolean;
};

const placementOf = ({ pinned, leadsBar }: BarPillPlacement) => ({
  ...(pinned && { pinned }),
  ...(leadsBar && { leadsBar }),
});

export type BarPillEntry = GroupBarPill | CustomBarPill;

type GetFilterFromGroupsProps = {
  id: FiltersGroupId;
  filterNames: string[];
  filtersGroups: FiltersGroups;
  label: string;
  icon?: ReactElement;
  columns?: number;
  applyOnChange?: boolean;
};

const filtersOf = (filtersGroups: FiltersGroups): Filter[] =>
  filtersGroups.flatMap((group) => ('filters' in group ? group.filters : []));

const pillFromFilters = (
  allFilters: Filter[],
  { id, filterNames, label, icon, columns, applyOnChange }: Omit<GetFilterFromGroupsProps, 'filtersGroups'>
): FilterPill | undefined => {
  const filters = allFilters.filter((f) => filterNames.includes(f.name));
  return filters.length
    ? {
        id,
        label,
        filters,
        ...(icon && { icon }),
        ...(columns && { columns }),
        ...(applyOnChange && { applyOnChange }),
      }
    : undefined;
};

export const getFilterPillFromGroupsByNames = ({
  filtersGroups,
  ...pill
}: GetFilterFromGroupsProps): FilterPill | undefined => pillFromFilters(filtersOf(filtersGroups), pill);

export const BAR_PILLS: BarPillEntry[] = [
  { id: CUSTOM_GROUP_NAME.ALL_FILTERS, CustomPill: AllFiltersPill, withIcon: true, pinned: true },
  { id: FILTER_NAME.SORT, CustomPill: SortPill, layouts: ['mobile'], leadsBar: true },
  { id: FILTER_NAME.PRICE_RANGE, filterNames: [FILTER_NAME.PRICE_RANGE], leadsBar: true },
  { id: FILTER_NAME.DESTINATION_AIRPORT, filterNames: [FILTER_NAME.DESTINATION_AIRPORT] },
  { id: FILTER_NAME.OVERNIGHT_FLIGHTS, filterNames: [FILTER_NAME.OVERNIGHT_FLIGHTS] },
  { id: FILTER_NAME.PROPERTY_TYPE, filterNames: [FILTER_NAME.PROPERTY_TYPE] },
  { id: FILTER_NAME.STARS, filterNames: [FILTER_NAME.STARS] },
  { id: FILTER_NAME.RATING, filterNames: [FILTER_NAME.RATING] },
  { id: FILTER_NAME.MEAL_PLAN, filterNames: [FILTER_NAME.MEAL_PLAN], columns: 3 },
  {
    id: CUSTOM_GROUP_NAME.FACILITIES,
    filterNames: [...ACCOMMODATION_FACILITY_FILTER_NAMES, ...FACILITY_GROUP_FILTER_NAMES],
  },
];

type BarAssemblyInput = Pick<BarPillContext, 'filtersGroups'>;

export const customPill = (entry: CustomBarPill, filters: Filter[] = []): FilterPill => ({
  id: entry.id,
  label: '',
  filters,
  ...(entry.withIcon && { icon: getPillIcon(entry.id) }),
  ...placementOf(entry),
  CustomPill: entry.CustomPill,
});

/** Group pill icons belong to the evo bar; the current bar keeps its pills wordmark-only. */
type BarAssemblyOptions = { groupIcons?: boolean };

const buildEntry = (
  entry: BarPillEntry,
  context: BarAssemblyInput,
  allFilters: Filter[],
  groupIcons: boolean
): FilterPill | undefined => {
  const ownGroup = context.filtersGroups.find((candidate) => candidate.id === entry.id);

  if (!('filterNames' in entry)) {
    return customPill(entry, ownGroup && 'filters' in ownGroup ? ownGroup.filters : []);
  }

  if (!ownGroup || !('label' in ownGroup)) return undefined;

  const pill = pillFromFilters(allFilters, {
    id: entry.id,
    filterNames: entry.filterNames,
    label: ownGroup.label,
    icon: groupIcons ? getPillIcon(entry.id) : undefined,
    columns: entry.columns,
  });

  return pill && { ...pill, ...placementOf(entry) };
};

export const assembleBarPills = (
  context: BarAssemblyInput,
  layout: BarLayout,
  { groupIcons = false }: BarAssemblyOptions = {}
): FilterPill[] => {
  const allFilters = filtersOf(context.filtersGroups);
  return BAR_PILLS.filter((entry) => !entry.layouts || entry.layouts.includes(layout))
    .map((entry) => buildEntry(entry, context, allFilters, groupIcons))
    .filter((pill): pill is FilterPill => !!pill);
};
